import React from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
  Card,
  CardContent,
  Paper,
  Checkbox,
  Menu,
  MenuItem,
  IconButton,
  FormControlLabel,
  Switch,
  Slider,
} from "@mui/material";
import {
  FreeBreakfast as BreakfastIcon,
  LunchDining as LunchIcon,
  DinnerDining as DinnerIcon,
  LocalCafe as SnackIcon,
  AutoAwesome as AIIcon,
  ContentCopy as CopyIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
} from "@mui/icons-material";
import { consola } from "consola";
import { nanoid } from "nanoid";
import { useRecoilValue } from "recoil";

import StandardButton from "../../../../components/StandardButton";
import { AIButton } from "../../../../components";
import useEntitlements from "../../../../hooks/useEntitlements";
import UpgradePrompt from "../../../../components/organisms/UpgradePrompt";
import { getChatGPTResponse } from "../../../../data/GlobalData";
import {
  MealPlanGenerationMessage,
  SingleRecipeForMealPlanMessage,
} from "../../../../data/ChatGPTMessages";
import { suggestCategory } from "../../../../data/ShoppingCategories";
import useRecipes from "../../../../hooks/useRecipes";
import organizationIdSelector from "../../../../atoms/organizationIdSelector";
import NewColors from "../../../../themes/NewColors";
import RecipePreviewDialog from "./components/mealplan/RecipePreviewDialog";

const MacroInputStack = ({ label, value, onChange }) => {
  return (
    <Card sx={{ mb: 0.5, p: 0.25 }}>
      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 0.55, fontWeight: 600, fontSize: "0.8rem" }}
        >
          {label}
        </Typography>
        <TextField
          label={`Daily ${label} (g)`}
          size="small"
          value={value}
          variant="outlined"
          type="number"
          fullWidth
          sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem" } }}
          onChange={(event) => {
            const val =
              event.target.value.length === 0
                ? 0
                : parseInt(event.target.value, 10);
            onChange(isNaN(val) ? 0 : val);
          }}
        />
      </CardContent>
    </Card>
  );
};

const CalorieBasedMacroCalculator = ({
  dailyCalories,
  onCaloriesChange,
  proteinPercent,
  onProteinPercentChange,
  carbPercent,
  onCarbPercentChange,
  fatPercent,
  onFatPercentChange,
  onApplyMacros,
  selectedDiets = [],
}) => {
  // Calculate grams from calories and percentages
  const proteinGrams = Math.round((dailyCalories * (proteinPercent / 100)) / 4);
  const carbGrams = Math.round((dailyCalories * (carbPercent / 100)) / 4);
  const fatGrams = Math.round((dailyCalories * (fatPercent / 100)) / 9);

  const totalPercent = proteinPercent + carbPercent + fatPercent;
  const isValidPercent = Math.abs(totalPercent - 100) < 0.1;

  // Define macro presets based on dietary restrictions
  const getMacroPresets = () => {
    const presets = [];

    // Add presets based on selected dietary restrictions
    if (selectedDiets.includes("balanced") || selectedDiets.length === 0) {
      presets.push({ name: "Balanced", protein: 30, carbs: 40, fats: 30 });
    }
    if (selectedDiets.includes("keto")) {
      presets.push({ name: "Keto", protein: 25, carbs: 5, fats: 70 });
    }
    if (selectedDiets.includes("low carb")) {
      presets.push({ name: "Low Carb", protein: 35, carbs: 15, fats: 50 });
    }
    if (selectedDiets.includes("high carb")) {
      presets.push({ name: "High Carb", protein: 25, carbs: 55, fats: 20 });
    }
    if (selectedDiets.includes("atkins")) {
      presets.push({ name: "Atkins", protein: 30, carbs: 10, fats: 60 });
    }
    if (selectedDiets.includes("paleo")) {
      presets.push({ name: "Paleo", protein: 30, carbs: 30, fats: 40 });
    }
    if (selectedDiets.includes("mediterranean")) {
      presets.push({ name: "Mediterranean", protein: 20, carbs: 45, fats: 35 });
    }
    if (selectedDiets.includes("fat free")) {
      presets.push({ name: "Fat Free", protein: 30, carbs: 65, fats: 5 });
    }

    // If no matching presets found, show default balanced option
    if (presets.length === 0) {
      presets.push({ name: "Balanced", protein: 30, carbs: 40, fats: 30 });
    }

    return presets;
  };

  const macroPresets = getMacroPresets();

  return (
    <Card sx={{ mb: 0.5, p: 0.25 }}>
      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, fontWeight: 600, fontSize: "0.8rem" }}
        >
          Calorie-Based Calculator
        </Typography>

        {/* Daily Calories Input */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.7rem", color: "text.secondary" }}
          >
            Daily Calories
          </Typography>
          <TextField
            size="small"
            value={dailyCalories}
            variant="outlined"
            type="number"
            fullWidth
            sx={{ "& .MuiInputBase-root": { fontSize: "0.875rem" }, mt: 0.5 }}
            onChange={(event) => {
              const val =
                event.target.value.length === 0
                  ? 0
                  : parseInt(event.target.value, 10);
              onCaloriesChange(isNaN(val) ? 0 : val);
            }}
          />
        </Box>

        {/* Macro Percentage Sliders */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: "0.7rem",
              color: "text.secondary",
              mb: 0.5,
              display: "block",
            }}
          >
            Quick Presets Based on Your Dietary Selections:
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}
          >
            {macroPresets.map((preset) => (
              <Button
                key={preset.name}
                size="small"
                variant="outlined"
                onClick={() => {
                  onProteinPercentChange(preset.protein);
                  onCarbPercentChange(preset.carbs);
                  onFatPercentChange(preset.fats);
                }}
                sx={{ fontSize: "0.6rem", py: 0.25, px: 0.5 }}
              >
                {preset.name}
              </Button>
            ))}
          </Stack>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.7rem", color: "text.secondary" }}
          >
            Protein: {proteinPercent}% = {proteinGrams}g
          </Typography>
          <Slider
            value={proteinPercent}
            onChange={(_, value) => onProteinPercentChange(value)}
            min={10}
            max={50}
            step={1}
            size="small"
            sx={{ color: "success.main" }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.7rem", color: "text.secondary" }}
          >
            Carbs: {carbPercent}% = {carbGrams}g
          </Typography>
          <Slider
            value={carbPercent}
            onChange={(_, value) => onCarbPercentChange(value)}
            min={10}
            max={70}
            step={1}
            size="small"
            sx={{ color: "warning.main" }}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography
            variant="caption"
            sx={{ fontSize: "0.7rem", color: "text.secondary" }}
          >
            Fats: {fatPercent}% = {fatGrams}g
          </Typography>
          <Slider
            value={fatPercent}
            onChange={(_, value) => onFatPercentChange(value)}
            min={15}
            max={60}
            step={1}
            size="small"
            sx={{ color: "info.main" }}
          />
        </Box>

        {/* Total Percentage Display */}
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.65rem",
            color: isValidPercent ? "success.main" : "error.main",
            fontWeight: 600,
            display: "block",
            mb: 1,
          }}
        >
          Total: {totalPercent.toFixed(1)}%{" "}
          {!isValidPercent && "(Should equal 100%)"}
        </Typography>

        {/* Apply Button */}
        <Button
          size="small"
          variant="contained"
          color="primary"
          fullWidth
          disabled={!isValidPercent || dailyCalories <= 0}
          onClick={() => onApplyMacros(proteinGrams, carbGrams, fatGrams)}
          sx={{ fontSize: "0.7rem", py: 0.5 }}
        >
          Apply These Macros
        </Button>
      </CardContent>
    </Card>
  );
};

const DIETARY_OPTIONS = [
  { value: "balanced", label: "Balanced" },
  { value: "keto", label: "Keto" },
  { value: "atkins", label: "Atkins" },
  { value: "low carb", label: "Low Carb" },
  { value: "high carb", label: "High Carb" },
  { value: "fat free", label: "Fat Free" },
  { value: "vegetarian", label: "Vegetarian" },
  { value: "vegan", label: "Vegan" },
  { value: "gluten free", label: "Gluten Free" },
  { value: "dairy free", label: "Dairy Free" },
  { value: "paleo", label: "Paleo" },
  { value: "clean", label: "Clean" },
  { value: "mediterranean", label: "Mediterranean" },
];

const DietaryRestrictionsSelector = ({ selectedDiets, onDietsChanged }) => {
  const toggleDiet = (dietValue) => {
    if (selectedDiets.includes(dietValue)) {
      onDietsChanged(selectedDiets.filter((diet) => diet !== dietValue));
    } else {
      onDietsChanged([...selectedDiets, dietValue]);
    }
  };

  return (
    <Card sx={{ mb: 0.5, p: 0.25 }}>
      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 0.5, fontWeight: 600, fontSize: "0.8rem" }}
        >
          Dietary Restrictions
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25 }}>
          {DIETARY_OPTIONS.map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              clickable
              size="small"
              color={
                selectedDiets.includes(option.value) ? "primary" : "default"
              }
              variant={
                selectedDiets.includes(option.value) ? "filled" : "outlined"
              }
              onClick={() => toggleDiet(option.value)}
              sx={{
                fontSize: "0.7rem",
                height: "24px",
                "&:hover": {
                  backgroundColor: selectedDiets.includes(option.value)
                    ? "primary.dark"
                    : "action.hover",
                },
              }}
            />
          ))}
        </Box>
        {selectedDiets.length === 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.25, display: "block", fontSize: "0.65rem" }}
          >
            Select one or more dietary restrictions, or leave empty for no
            restrictions
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const CustomInstructionsInput = ({
  customInstructions,
  onCustomInstructionsChanged,
  onGenerateDay,
  generatingDay,
}) => {
  const { hasFeature, organizationData } = useEntitlements();
  const canUseMealPlanAi = hasFeature("meal_plan_data_entry_assistance");

  return (
    <Card sx={{ mt: 1, mb: 1, p: 0.5 }}>
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Typography
          variant="subtitle2"
          sx={{ mb: 1, fontWeight: 600, fontSize: "0.875rem" }}
        >
          Custom Instructions
        </Typography>
        <TextField
          multiline
          rows={2}
          fullWidth
          size="small"
          placeholder='e.g., "No nuts", "Quick 15-min meals", "High volume foods"'
          value={customInstructions}
          onChange={(event) => onCustomInstructionsChanged(event.target.value)}
          variant="outlined"
          sx={{ fontSize: "0.875rem" }}
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block", fontSize: "0.7rem" }}
        >
          Add any specific preferences or requirements for your meal plan
        </Typography>
        
        <Box sx={{ mt: 2 }}>
          {canUseMealPlanAi ? (
            <AIButton
              fullWidth
              size="small"
              loading={generatingDay === "monday"}
              onClick={() => onGenerateDay("monday")}
              disabled={generatingDay !== null}
              sx={{
                fontSize: "0.875rem",
                py: 1,
              }}
            >
              Generate Monday
            </AIButton>
          ) : (
            <UpgradePrompt
              organizationData={organizationData}
              minTier="pro"
              variant="banner"
              title="Upgrade for AI meal plans"
              description="AI meal plan generation is available on Pro."
              featureName="meal_plan_data_entry_assistance"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const RecipeSelectionDialog = ({
  open,
  onClose,
  recipes,
  generatedRecipes,
  onSelectRecipe,
  mealType,
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [selectedTab, setSelectedTab] = React.useState(0); // 0 = existing, 1 = generated

  const filteredExistingRecipes = recipes.filter((recipe) => {
    // Filter by search term
    const matchesSearch =
      recipe.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipe.n?.toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by meal type: show recipes matching the target mealType OR "other" meal types
    // For example, when adding to breakfast, show breakfast + other recipes
    // Also include recipes without a mealType set (treat them as "other")
    const recipeMealType = recipe.mealType || "other";
    const matchesMealType = recipeMealType === mealType || recipeMealType === "other";

    return matchesSearch && matchesMealType;
  });

  const filteredGeneratedRecipes = generatedRecipes.filter((recipe) =>
    recipe.n?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Helper function to normalize and display macros for any recipe
  const getRecipeMacros = (recipe) => {
    // First check if we have top-level macros (Firebase format)
    if (
      recipe.kCals !== undefined ||
      recipe.proteins !== undefined ||
      recipe.carbs !== undefined ||
      recipe.fats !== undefined
    ) {
      return {
        kCals: recipe.kCals || 0,
        p: recipe.proteins || 0,
        c: recipe.carbs || 0,
        f: recipe.fats || 0,
        servings: recipe.servings || 1,
      };
    }

    // If we have existing mac object
    if (recipe.mac) {
      return {
        kCals: recipe.mac.kCals || 0,
        p: recipe.mac.p || 0,
        c: recipe.mac.c || 0,
        f: recipe.mac.f || 0,
        servings: recipe.ns || recipe.servings || 1,
      };
    }

    // If no top-level macros but we have ingredients, calculate from ingredients
    if (
      recipe.ingredients &&
      Array.isArray(recipe.ingredients) &&
      recipe.ingredients.length > 0
    ) {
      const calculated = recipe.ingredients.reduce(
        (totals, ingredient) => {
          return {
            kCals:
              totals.kCals +
              (ingredient.proteins || 0) * 4 +
              (ingredient.carbs || 0) * 4 +
              (ingredient.fats || 0) * 9,
            p: totals.p + (ingredient.proteins || 0),
            c: totals.c + (ingredient.carbs || 0),
            f: totals.f + (ingredient.fats || 0),
          };
        },
        { kCals: 0, p: 0, c: 0, f: 0 },
      );

      return {
        ...calculated,
        servings: recipe.servings || 1,
      };
    }

    // Fallback to zero values
    return {
      kCals: 0,
      p: 0,
      c: 0,
      f: 0,
      servings: recipe.servings || recipe.ns || 1,
    };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Recipe to {mealType}</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <Button
              variant={selectedTab === 0 ? "contained" : "outlined"}
              onClick={() => setSelectedTab(0)}
              size="small"
            >
              Existing Recipes ({filteredExistingRecipes.length})
            </Button>
            <Button
              variant={selectedTab === 1 ? "contained" : "outlined"}
              onClick={() => setSelectedTab(1)}
              size="small"
            >
              Generated Recipes ({filteredGeneratedRecipes.length})
            </Button>
          </Box>
        </Box>

        <TextField
          fullWidth
          label="Search recipes"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Box sx={{ maxHeight: "400px", overflow: "auto" }}>
          {selectedTab === 0 && (
            <>
              {filteredExistingRecipes.map((recipe, index) => {
                const macros = getRecipeMacros(recipe);
                return (
                  <Card
                    key={index}
                    sx={{ mb: 1, cursor: "pointer" }}
                    onClick={() => onSelectRecipe(recipe, "existing")}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {recipe.n || recipe.name}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, mt: 1, mb: 1 }}>
                        <Chip
                          label="From Library"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                        {recipe.mealType && (
                          <Chip
                            label={recipe.mealType}
                            size="small"
                            color="info"
                            variant="outlined"
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          label={`${Math.round(macros.kCals / macros.servings)} cal`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${Math.round(macros.p / macros.servings)}P`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${Math.round(macros.c / macros.servings)}C`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${Math.round(macros.f / macros.servings)}F`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${macros.servings} serving${macros.servings !== 1 ? "s" : ""}`}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              {filteredExistingRecipes.length === 0 && (
                <Typography
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  No {mealType} recipes found matching &quot;{searchTerm}&quot;
                </Typography>
              )}
            </>
          )}

          {selectedTab === 1 && (
            <>
              {filteredGeneratedRecipes.map((recipe, index) => (
                <Card
                  key={index}
                  sx={{ mb: 1, cursor: "pointer" }}
                  onClick={() => onSelectRecipe(recipe, "generated")}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {recipe.n}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, mt: 1, mb: 1 }}>
                      <Chip
                        label={
                          recipe.isExisting ? "From Library" : "AI Generated"
                        }
                        size="small"
                        color={recipe.isExisting ? "primary" : "secondary"}
                        variant="outlined"
                      />
                      <Chip
                        label={recipe.mealType}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </Box>
                    {recipe.mac && (
                      <Box
                        sx={{
                          mt: 1,
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <Chip
                          label={`${Math.round(recipe.mac.kCals / (recipe.ns || 1))} cal`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${Math.round(recipe.mac.p / (recipe.ns || 1))}P`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${Math.round(recipe.mac.c / (recipe.ns || 1))}C`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${Math.round(recipe.mac.f / (recipe.ns || 1))}F`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${recipe.ns || 1} serving${(recipe.ns || 1) !== 1 ? "s" : ""}`}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
              {filteredGeneratedRecipes.length === 0 && (
                <Typography
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  No generated recipes found matching &quot;{searchTerm}&quot;
                </Typography>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};

const WeeklyMealPlanView = ({
  generatedMealPlan,
  onRecipeToggle,
  onAddRecipe,
  onRemoveRecipe,
  onAddExistingRecipe,
  onGenerateDay,
  onCopyMealToDay,
  onRecipePreview,
  generatingRecipe,
  generatingDay,
  dailyProteins,
  dailyCarbs,
  dailyFats,
  calculateDayMacros,
}) => {
  const { hasFeature, organizationData } = useEntitlements();
  const canUseMealPlanAi = hasFeature("meal_plan_data_entry_assistance");
  const dayNames = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  const mealTypes = ["breakfast", "lunch", "dinner", "snack"];
  const mealIcons = {
    breakfast: BreakfastIcon,
    lunch: LunchIcon,
    dinner: DinnerIcon,
    snack: SnackIcon,
  };

  // State for copy menu
  const [copyMenuAnchor, setCopyMenuAnchor] = React.useState(null);
  const [copySource, setCopySource] = React.useState(null); // { mealType, day }

  // State for day copy menu
  const [dayCopyMenuAnchor, setDayCopyMenuAnchor] = React.useState(null);
  const [dayCopySource, setDayCopySource] = React.useState(null); // { day }

  const handleCopyMenuOpen = (event, mealType, day) => {
    setCopyMenuAnchor(event.currentTarget);
    setCopySource({ mealType, day });
  };

  const handleCopyMenuClose = () => {
    setCopyMenuAnchor(null);
    setCopySource(null);
  };

  const handleCopyToDay = (targetDay) => {
    if (copySource && onCopyMealToDay) {
      onCopyMealToDay(copySource.mealType, copySource.day, targetDay);
    }
    handleCopyMenuClose();
  };

  const handleCopyToAllDays = () => {
    if (copySource && onCopyMealToDay) {
      // Copy to all days except the source day
      dayNames
        .filter((targetDay) => targetDay !== copySource.day)
        .forEach((targetDay) => {
          onCopyMealToDay(copySource.mealType, copySource.day, targetDay);
        });
    }
    handleCopyMenuClose();
  };

  // Day copy handlers
  const handleDayCopyMenuOpen = (event, day) => {
    setDayCopyMenuAnchor(event.currentTarget);
    setDayCopySource({ day });
  };

  const handleDayCopyMenuClose = () => {
    setDayCopyMenuAnchor(null);
    setDayCopySource(null);
  };

  const handleCopyDayToDay = (targetDay) => {
    if (dayCopySource) {
      // Copy all meals from source day to target day
      const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];
      mealTypes.forEach(mealType => {
        if (onCopyMealToDay) {
          onCopyMealToDay(mealType, dayCopySource.day, targetDay);
        }
      });
    }
    handleDayCopyMenuClose();
  };

  const handleCopyDayToAllDays = () => {
    if (dayCopySource) {
      const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];
      // Copy to all days except the source day
      dayNames
        .filter((targetDay) => targetDay !== dayCopySource.day)
        .forEach((targetDay) => {
          mealTypes.forEach(mealType => {
            if (onCopyMealToDay) {
              onCopyMealToDay(mealType, dayCopySource.day, targetDay);
            }
          });
        });
    }
    handleDayCopyMenuClose();
  };

  if (!generatedMealPlan?.mealPlan) return null;

  return (
    <Box>
      {!canUseMealPlanAi ? (
        <UpgradePrompt
          organizationData={organizationData}
          minTier="pro"
          variant="banner"
          title="Upgrade for AI meal plan generation"
          description="Generate recipes with AI on Pro. You can still browse and assign existing recipes."
          featureName="meal_plan_data_entry_assistance"
        />
      ) : null}
      {dayNames.map((day, dayIndex) => {
        const dayMacros = calculateDayMacros(day);

        return (
          <Card key={day} sx={{ mb: dayIndex === dayNames.length - 1 ? 0 : 2 }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "primary.main" }}
                  >
                    {day}
                  </Typography>
                  
                  {/* Day Copy Button */}
                  <IconButton
                    size="small"
                    onClick={(e) => handleDayCopyMenuOpen(e, day)}
                    sx={{
                      backgroundColor: "action.hover",
                      "&:hover": { backgroundColor: "action.selected" },
                      ml: 0.5
                    }}
                    title={`Copy entire ${day} to other days`}
                  >
                    <CopyIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Box>

                {/* Daily Macro Summary */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    label={`${dayMacros.totalKCals} cal`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem" }}
                  />
                  <Chip
                    label={`${dayMacros.totalP}/${dailyProteins}P`}
                    size="small"
                    variant="outlined"
                    color={
                      dayMacros.totalP >= dailyProteins * 0.9
                        ? "success"
                        : "default"
                    }
                    sx={{ fontSize: "0.7rem" }}
                  />
                  <Chip
                    label={`${dayMacros.totalC}/${dailyCarbs}C`}
                    size="small"
                    variant="outlined"
                    color={
                      dayMacros.totalC >= dailyCarbs * 0.9
                        ? "success"
                        : "default"
                    }
                    sx={{ fontSize: "0.7rem" }}
                  />
                  <Chip
                    label={`${dayMacros.totalF}/${dailyFats}F`}
                    size="small"
                    variant="outlined"
                    color={
                      dayMacros.totalF >= dailyFats * 0.9
                        ? "success"
                        : "default"
                    }
                    sx={{ fontSize: "0.7rem" }}
                  />
                  {dayMacros.totalFi > 0 && (
                    <Chip
                      label={`${dayMacros.totalFi}g Fi`}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  )}
                  <Chip
                    label={
                      dayMacros.isComplete
                        ? "Complete"
                        : `${dayMacros.completedMeals}/4 meals`
                    }
                    size="small"
                    color={dayMacros.isComplete ? "success" : "warning"}
                    variant={dayMacros.isComplete ? "filled" : "outlined"}
                    sx={{ fontSize: "0.7rem", fontWeight: 600 }}
                  />
                  {canUseMealPlanAi ? (
                    <AIButton
                      loading={generatingDay === day.toLowerCase()}
                      onClick={() => onGenerateDay(day.toLowerCase())}
                      disabled={generatingDay !== null}
                      sx={{
                        fontSize: "0.65rem",
                        py: 0.25,
                        px: 1,
                        minHeight: "24px",
                        ml: 1,
                      }}
                    >
                      {generatingDay === day.toLowerCase()
                        ? "Generating..."
                        : `Generate ${day}`}
                    </AIButton>
                  ) : null}
                </Box>
              </Box>

              <Grid container spacing={2}>
                {mealTypes.map((mealType) => {
                  const Icon = mealIcons[mealType];
                  const recipes = generatedMealPlan.mealPlan[mealType] || [];

                  // Get the recipe indexes for this day based on weekly schedule (now an array)
                  const dayRecipeIndexes =
                    generatedMealPlan.weeklySchedule?.[day.toLowerCase()]?.[
                      mealType
                    ] || [];
                  const dayRecipes = dayRecipeIndexes
                    .map((index) => recipes[index])
                    .filter((recipe) => recipe); // Filter out undefined recipes

                  return (
                    <Grid item xs={12} sm={6} md={3} key={mealType}>
                      <Paper
                        sx={{
                          p: 1.5,
                          height: "320px",
                          display: "flex",
                          flexDirection: "column",
                          border: "1px solid",
                          borderColor: "grey.300",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Icon
                              sx={{
                                mr: 1,
                                fontSize: "1.2rem",
                                color: "primary.main",
                              }}
                            />
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                textTransform: "capitalize",
                                fontSize: "0.9rem",
                              }}
                            >
                              {mealType}
                            </Typography>
                          </Box>
                          {dayRecipes.length > 0 && (
                            <IconButton
                              size="small"
                              onClick={(e) =>
                                handleCopyMenuOpen(e, mealType, day)
                              }
                              sx={{
                                p: 0.25,
                                color: "primary.main",
                                "&:hover": {
                                  backgroundColor: "primary.light",
                                  color: "white",
                                },
                              }}
                              title={`Copy ${mealType} to another day`}
                            >
                              <CopyIcon sx={{ fontSize: "0.9rem" }} />
                            </IconButton>
                          )}
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            overflowY: "auto",
                            overflowX: "hidden",
                            mb: 1,
                            border: "1px solid",
                            borderColor: "grey.200",
                            borderRadius: 1,
                            p: 0.5,
                            minHeight: "180px",
                          }}
                        >
                          {dayRecipes.length > 0 ? (
                            <Stack spacing={0.5}>
                              {dayRecipes.map((dayRecipe, recipeIndex) => (
                                <Box
                                  key={recipeIndex}
                                  sx={{
                                    border: "1px solid",
                                    borderColor: "grey.300",
                                    borderRadius: 1,
                                    p: 1,
                                    bgcolor: "background.paper",
                                    position: "relative",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-start",
                                      gap: 1,
                                      minHeight: "20px",
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={dayRecipe.selected !== false}
                                      onChange={(e) => {
                                        const actualIndex = recipes.findIndex(
                                          (r) => r === dayRecipe,
                                        );
                                        onRecipeToggle(
                                          mealType,
                                          actualIndex,
                                          e.target.checked,
                                        );
                                      }}
                                      sx={{ p: 0, mt: 0.25 }}
                                    />

                                    <Box sx={{ flex: 1, minWidth: 0, pr: 3 }}>
                                      <Typography
                                        variant="body2"
                                        onClick={() =>
                                          onRecipePreview(dayRecipe)
                                        }
                                        sx={{
                                          fontWeight: 500,
                                          fontSize: "0.75rem",
                                          lineHeight: 1.2,
                                          wordBreak: "break-word",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          display: "-webkit-box",
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: "vertical",
                                          cursor: "pointer",
                                          color: "primary.main",
                                          textDecoration: "none",
                                          borderBottom: "1px solid transparent",
                                          transition: "all 0.2s ease",
                                          "&:hover": {
                                            color: "white",
                                            backgroundColor: "primary.main",
                                            borderRadius: "4px",
                                            padding: "2px 4px",
                                            margin: "-2px -4px",
                                            borderBottom: "1px solid white",
                                            transform: "scale(1.02)",
                                          },
                                        }}
                                      >
                                        {dayRecipe.n}
                                      </Typography>

                                      <Box
                                        sx={{
                                          mt: 0.5,
                                          display: "flex",
                                          gap: 0.25,
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        {dayRecipe.isExisting && (
                                          <Chip
                                            label="Existing"
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            sx={{
                                              fontSize: "0.5rem",
                                              height: "14px",
                                            }}
                                          />
                                        )}
                                        <Chip
                                          label={`${Math.round(dayRecipe.mac.kCals / dayRecipe.ns)} cal`}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            fontSize: "0.5rem",
                                            height: "14px",
                                          }}
                                        />
                                        <Chip
                                          label={`${(dayRecipe.mac.p / dayRecipe.ns).toFixed(0)}P`}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            fontSize: "0.5rem",
                                            height: "14px",
                                          }}
                                        />
                                        <Chip
                                          label={`${(dayRecipe.mac.c / dayRecipe.ns).toFixed(0)}C`}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            fontSize: "0.5rem",
                                            height: "14px",
                                          }}
                                        />
                                        <Chip
                                          label={`${(dayRecipe.mac.f / dayRecipe.ns).toFixed(0)}F`}
                                          size="small"
                                          variant="outlined"
                                          sx={{
                                            fontSize: "0.5rem",
                                            height: "14px",
                                          }}
                                        />
                                      </Box>
                                    </Box>

                                    <Button
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        onRemoveRecipe(
                                          mealType,
                                          day,
                                          recipeIndex,
                                        )
                                      }
                                      sx={{
                                        position: "absolute",
                                        top: 2,
                                        right: 2,
                                        minWidth: "16px",
                                        width: "16px",
                                        height: "16px",
                                        p: 0,
                                        fontSize: "0.6rem",
                                        borderRadius: "50%",
                                        zIndex: 1,
                                      }}
                                    >
                                      ×
                                    </Button>
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontStyle: "italic",
                                fontSize: "0.8rem",
                                textAlign: "center",
                                pt: 2,
                              }}
                            >
                              No recipes assigned for {day}
                            </Typography>
                          )}
                        </Box>

                        <Stack spacing={0.5}>
                          {canUseMealPlanAi ? (
                            <StandardButton
                              size="small"
                              loading={
                                generatingRecipe?.mealType === mealType &&
                                generatingRecipe?.day === day
                              }
                              loadingText="Generating..."
                              onClick={() => onAddRecipe(mealType, day, dayIndex)}
                              disabled={
                                generatingRecipe?.mealType === mealType &&
                                generatingRecipe?.day === day
                              }
                              sx={{
                                fontSize: "0.7rem",
                                py: 0.5,
                                minHeight: "32px",
                              }}
                            >
                              + Generate Recipe
                            </StandardButton>
                          ) : null}
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() =>
                              onAddExistingRecipe(mealType, day, dayIndex)
                            }
                            sx={{
                              fontSize: "0.7rem",
                              py: 0.5,
                              minHeight: "32px",
                            }}
                          >
                            + Browse Recipes
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        );
      })}

      {/* Copy Menu */}
      <Menu
        anchorEl={copyMenuAnchor}
        open={Boolean(copyMenuAnchor)}
        onClose={handleCopyMenuClose}
        PaperProps={{
          sx: { maxHeight: 300 },
        }}
      >
        {/* Copy to All Days option */}
        <MenuItem
          onClick={handleCopyToAllDays}
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "primary.main",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          Copy to All Days
        </MenuItem>

        {/* Individual day options */}
        {dayNames
          .filter((targetDay) => copySource && targetDay !== copySource.day)
          .map((targetDay) => (
            <MenuItem
              key={targetDay}
              onClick={() => handleCopyToDay(targetDay)}
              sx={{ fontSize: "0.875rem" }}
            >
              Copy to {targetDay}
            </MenuItem>
          ))}
      </Menu>

      {/* Day Copy Menu */}
      <Menu
        anchorEl={dayCopyMenuAnchor}
        open={Boolean(dayCopyMenuAnchor)}
        onClose={handleDayCopyMenuClose}
        PaperProps={{
          sx: { maxHeight: 300 },
        }}
      >
        {/* Copy to All Days option */}
        <MenuItem
          onClick={handleCopyDayToAllDays}
          sx={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "primary.main",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          Copy to All Days
        </MenuItem>

        {/* Individual day options */}
        {dayNames
          .filter((targetDay) => dayCopySource && targetDay !== dayCopySource.day)
          .map((targetDay) => (
            <MenuItem
              key={targetDay}
              onClick={() => handleCopyDayToDay(targetDay)}
              sx={{ fontSize: "0.875rem" }}
            >
              Copy to {targetDay}
            </MenuItem>
          ))}
      </Menu>
    </Box>
  );
};


const MealPlanGeneratorDialog = ({
  onDialogClosed,
  existingMealPlan = null,
}) => {
  // Determine if we're in edit mode
  const isEditMode = Boolean(existingMealPlan);

  // Get organization ID
  const organizationId = useRecoilValue(organizationIdSelector);

  // For meal plan generation, we'll use all recipes
  const { recipes } = useRecipes({
    organizationId,
  });

  // State for meal plan specific recipes (loaded individually)
  const [, setMealPlanRecipes] = React.useState([]);

  const [dailyProteins, setDailyProteins] = React.useState(
    existingMealPlan?.macroTargets?.protein || 150,
  );
  const [dailyCarbs, setDailyCarbs] = React.useState(
    existingMealPlan?.macroTargets?.carbs || 200,
  );
  const [dailyFats, setDailyFats] = React.useState(
    existingMealPlan?.macroTargets?.fats || 70,
  );
  const [selectedDiets, setSelectedDiets] = React.useState(
    existingMealPlan?.dietaryRestrictions || [],
  );
  const [customInstructions, setCustomInstructions] = React.useState(
    existingMealPlan?.customInstructions || "",
  );
  const [mealPlanName, setMealPlanName] = React.useState(
    existingMealPlan?.name || "",
  );
  const [mealPlanDescription, setMealPlanDescription] = React.useState(
    existingMealPlan?.description || "",
  );
  const [isPublic, setIsPublic] = React.useState(
    existingMealPlan?.isPublic || false,
  );

  // Calorie-based macro calculator state - restore from existing meal plan if available
  const [useBulkMode, setUseBulkMode] = React.useState(
    existingMealPlan?.calculatorSettings?.useBulkMode || false,
  );
  const [bulkCalories, setBulkCalories] = React.useState(
    existingMealPlan?.calculatorSettings?.bulkCalories || 2000,
  );
  const [bulkProteinPercent, setBulkProteinPercent] = React.useState(
    existingMealPlan?.calculatorSettings?.bulkProteinPercent || 30,
  );
  const [bulkCarbPercent, setBulkCarbPercent] = React.useState(
    existingMealPlan?.calculatorSettings?.bulkCarbPercent || 40,
  );
  const [bulkFatPercent, setBulkFatPercent] = React.useState(
    existingMealPlan?.calculatorSettings?.bulkFatPercent || 30,
  );

  const [generatedMealPlan, setGeneratedMealPlan] = React.useState(null);
  const [generatingDay, setGeneratingDay] = React.useState(null); // Track which day is being generated
  const [generatingRecipe, setGeneratingRecipe] = React.useState(null); // { mealType, day, dayIndex }
  const [recipeSelectionDialog, setRecipeSelectionDialog] =
    React.useState(null); // { open, mealType, day, dayIndex }
  const [recipePreviewDialog, setRecipePreviewDialog] = React.useState(null); // { open, recipe }
  const [isSaving, setIsSaving] = React.useState(false); // Track save operation
  
  // Panel collapse state
  const [leftPanelCollapsed, setLeftPanelCollapsed] = React.useState(false);

  // Function to fetch specific recipes by IDs
  const fetchRecipesByIds = React.useCallback(
    async (recipeIds) => {
      if (!organizationId || !recipeIds || recipeIds.length === 0) {
        return [];
      }

      try {
        const firebase = (await import("firebase/compat/app")).default;
        const recipePromises = recipeIds.map(async (recipeId) => {
          const recipeDoc = await firebase
            .firestore()
            .collection("organizations")
            .doc(organizationId)
            .collection("recipes")
            .doc(recipeId)
            .get();

          if (recipeDoc.exists) {
            return { id: recipeDoc.id, ...recipeDoc.data() };
          }
          return null;
        });

        const fetchedRecipes = await Promise.all(recipePromises);
        return fetchedRecipes.filter((recipe) => recipe !== null);
      } catch (error) {
        console.error("Error fetching recipes by IDs:", error);
        return [];
      }
    },
    [organizationId],
  );

  // Initialize meal plan state from existing meal plan or create empty structure
  React.useEffect(() => {
    if (existingMealPlan) {
      console.log("Loading existing meal plan:", existingMealPlan);

      // Extract recipe IDs from selectedRecipes
      const recipeIds =
        existingMealPlan.selectedRecipes?.map((sr) => sr.recipeId) || [];

      if (recipeIds.length > 0) {
        console.log("Fetching specific recipes for meal plan:", recipeIds);

        fetchRecipesByIds(recipeIds).then((fetchedRecipes) => {
          console.log(
            "Fetched meal plan recipes:",
            fetchedRecipes.map((r) => ({
              id: r.id,
              name: r.name,
              mealType: r.mealType,
            })),
          );
          setMealPlanRecipes(fetchedRecipes);

          // Convert Firebase meal plan format to local generator format
          const convertedMealPlan = convertFirebaseMealPlanToLocal(
            existingMealPlan,
            fetchedRecipes,
          );
          setGeneratedMealPlan(convertedMealPlan);
        });
      } else {
        // No recipes to fetch, just convert empty meal plan
        const convertedMealPlan = convertFirebaseMealPlanToLocal(
          existingMealPlan,
          [],
        );
        setGeneratedMealPlan(convertedMealPlan);
      }
    } else {
      // Create empty meal plan structure for manual creation
      setGeneratedMealPlan({
        mealPlan: {
          breakfast: [],
          lunch: [],
          dinner: [],
          snack: [],
        },
        weeklySchedule: createEmptyWeeklySchedule(),
        analysis: null,
      });
    }
  }, [existingMealPlan, fetchRecipesByIds]);

  // Function to convert Firebase meal plan to local generator format
  const convertFirebaseMealPlanToLocal = (
    firebaseMealPlan,
    mealPlanSpecificRecipes = [],
  ) => {
    console.log("Converting Firebase meal plan:", firebaseMealPlan);
    console.log(
      "Available meal plan recipes:",
      mealPlanSpecificRecipes.length,
      mealPlanSpecificRecipes.map((r) => ({
        id: r.id,
        name: r.name,
        mealType: r.mealType,
      })),
    );

    // Initialize local format structure
    const localMealPlan = {
      mealPlan: {
        breakfast: [],
        lunch: [],
        dinner: [],
        snack: [],
      },
      weeklySchedule: createEmptyWeeklySchedule(),
      analysis: null,
    };

    // If the meal plan has selectedRecipes (array of recipe assignments),
    // we need to convert this to the generator format
    if (
      firebaseMealPlan.selectedRecipes &&
      Array.isArray(firebaseMealPlan.selectedRecipes)
    ) {
      console.log(
        "Processing selectedRecipes array:",
        firebaseMealPlan.selectedRecipes,
      );

      // Process each selected recipe
      firebaseMealPlan.selectedRecipes.forEach((selectedRecipe, index) => {
        console.log(`Processing selected recipe ${index}:`, selectedRecipe);

        const { recipeId, days = [], servingsPerDay = 1 } = selectedRecipe;

        // Find the actual recipe data from our fetched meal plan recipes
        const existingRecipe = mealPlanSpecificRecipes.find(
          (r) => r.id === recipeId,
        );

        if (existingRecipe) {
          console.log(
            "Processing existing recipe:",
            existingRecipe.name,
            "Original mealType:",
            existingRecipe.mealType,
          );

          // Convert existing recipe to generator format
          const generatorRecipe = {
            ...existingRecipe,
            n: existingRecipe.name,
            ns: servingsPerDay,
            mac: {
              kCals: parseFloat(existingRecipe.kCals) || 0,
              p: parseFloat(existingRecipe.proteins) || 0,
              c: parseFloat(existingRecipe.carbs) || 0,
              f: parseFloat(existingRecipe.fats) || 0,
            },
            selected: true,
            isExisting: true,
          };

          // Use the recipe's mealType directly from the database
          const mealType = existingRecipe.mealType || "other";
          console.log("Using mealType for", existingRecipe.name, ":", mealType);

          // Ensure the meal type array exists
          if (!localMealPlan.mealPlan[mealType]) {
            localMealPlan.mealPlan[mealType] = [];
          }

          // Add recipe to the appropriate meal type array
          localMealPlan.mealPlan[mealType].push(generatorRecipe);
          const currentRecipeIndex =
            localMealPlan.mealPlan[mealType].length - 1;

          // Add to weekly schedule based on specified days
          const dayNames = [
            "monday",
            "tuesday",
            "wednesday",
            "thursday",
            "friday",
            "saturday",
            "sunday",
          ];

          if (days.includes("all")) {
            // Add to all days
            dayNames.forEach((day) => {
              if (!localMealPlan.weeklySchedule[day][mealType]) {
                localMealPlan.weeklySchedule[day][mealType] = [];
              }
              localMealPlan.weeklySchedule[day][mealType].push(
                currentRecipeIndex,
              );
            });
          } else {
            // Add to specific days
            days.forEach((day) => {
              const dayLower = day.toLowerCase();
              if (dayNames.includes(dayLower)) {
                if (!localMealPlan.weeklySchedule[dayLower][mealType]) {
                  localMealPlan.weeklySchedule[dayLower][mealType] = [];
                }
                localMealPlan.weeklySchedule[dayLower][mealType].push(
                  currentRecipeIndex,
                );
              }
            });
          }

          console.log(
            `Added recipe "${generatorRecipe.n}" to ${mealType} for days:`,
            days,
          );
        } else {
          console.warn(
            `Recipe with ID ${recipeId} not found in fetched recipes`,
          );
          console.log(
            "Available recipe IDs:",
            mealPlanSpecificRecipes.map((r) => r.id),
          );
          console.log("Looking for recipe ID:", recipeId);

          // Create placeholder for missing recipe
          const placeholderRecipe = {
            id: recipeId,
            n: selectedRecipe.name || `Recipe ${recipeId}`,
            ns: servingsPerDay,
            mac: { kCals: 0, p: 0, c: 0, f: 0 },
            selected: true,
            isExisting: true,
            ingredients: [],
            instructions: "Recipe details not found",
          };

          const mealType = "other"; // Default meal type for missing recipes

          if (!localMealPlan.mealPlan[mealType]) {
            localMealPlan.mealPlan[mealType] = [];
          }

          localMealPlan.mealPlan[mealType].push(placeholderRecipe);
          const currentRecipeIndex =
            localMealPlan.mealPlan[mealType].length - 1;

          // Add to weekly schedule
          const dayNames = [
            "monday",
            "tuesday",
            "wednesday",
            "thursday",
            "friday",
            "saturday",
            "sunday",
          ];

          if (days.includes("all")) {
            dayNames.forEach((day) => {
              if (!localMealPlan.weeklySchedule[day][mealType]) {
                localMealPlan.weeklySchedule[day][mealType] = [];
              }
              localMealPlan.weeklySchedule[day][mealType].push(
                currentRecipeIndex,
              );
            });
          } else {
            days.forEach((day) => {
              const dayLower = day.toLowerCase();
              if (dayNames.includes(dayLower)) {
                if (!localMealPlan.weeklySchedule[dayLower][mealType]) {
                  localMealPlan.weeklySchedule[dayLower][mealType] = [];
                }
                localMealPlan.weeklySchedule[dayLower][mealType].push(
                  currentRecipeIndex,
                );
              }
            });
          }
        }
      });

      console.log("Converted meal plan structure:", localMealPlan);
    }

    return localMealPlan;
  };

  const createEmptyWeeklySchedule = () => {
    const schedule = {};
    const dayNames = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    const mealTypes = ["breakfast", "lunch", "dinner", "snack"];

    dayNames.forEach((day) => {
      schedule[day] = {};
      mealTypes.forEach((mealType) => {
        schedule[day][mealType] = [];
      });
    });

    return schedule;
  };

  // Handler to apply calculated macros from calorie calculator
  const handleApplyBulkMacros = (proteinGrams, carbGrams, fatGrams) => {
    setDailyProteins(proteinGrams);
    setDailyCarbs(carbGrams);
    setDailyFats(fatGrams);
  };

  const dailyCalories = React.useMemo(() => {
    return dailyProteins * 4 + dailyCarbs * 4 + dailyFats * 9;
  }, [dailyProteins, dailyCarbs, dailyFats]);

  // Calculate daily macros for a specific day - shared function
  const calculateDayMacros = React.useCallback(
    (day) => {
      if (!generatedMealPlan?.mealPlan)
        return {
          totalKCals: 0,
          totalP: 0,
          totalC: 0,
          totalF: 0,
          totalFi: 0,
          completedMeals: 0,
          isComplete: false,
        };

      let totalKCals = 0,
        totalP = 0,
        totalC = 0,
        totalF = 0,
        totalFi = 0;
      let completedMeals = 0;
      const mealTypes = ["breakfast", "lunch", "dinner", "snack"];

      mealTypes.forEach((mealType) => {
        const recipes = generatedMealPlan.mealPlan[mealType] || [];
        const dayRecipeIndexes =
          generatedMealPlan.weeklySchedule?.[day.toLowerCase()]?.[mealType] ||
          [];
        const dayRecipes = dayRecipeIndexes
          .map((index) => recipes[index])
          .filter((recipe) => recipe && recipe.selected !== false);

        if (dayRecipes.length > 0) {
          completedMeals++;
          // Sum all recipes for this meal type for the day
          dayRecipes.forEach((recipe) => {
            const fiRaw =
              recipe.mac?.fi ?? recipe.mac?.fiber ?? 0;
            const fiNum =
              typeof fiRaw === "number"
                ? fiRaw
                : parseFloat(String(fiRaw)) || 0;
            totalKCals += recipe.mac.kCals / recipe.ns;
            totalP += recipe.mac.p / recipe.ns;
            totalC += recipe.mac.c / recipe.ns;
            totalF += recipe.mac.f / recipe.ns;
            totalFi += fiNum / recipe.ns;
          });
        }
      });

      return {
        totalKCals: Math.round(totalKCals),
        totalP: Math.round(totalP),
        totalC: Math.round(totalC),
        totalF: Math.round(totalF),
        totalFi: Math.round(totalFi),
        completedMeals,
        isComplete: completedMeals === 4,
      };
    },
    [generatedMealPlan],
  );

  const analyzeExistingRecipes = (recipes, dietaryRestrictions) => {
    return recipes.map((recipe) => {
      const detectedRestrictions = [];

      // Analyze ingredients for dietary restrictions
      const ingredients = recipe.ingredients || [];
      const ingredientNames = ingredients
        .map((ing) => ing.name?.toLowerCase() || "")
        .join(" ");

      // Check for common dietary restrictions based on ingredients
      if (
        !ingredientNames.includes("meat") &&
        !ingredientNames.includes("chicken") &&
        !ingredientNames.includes("beef") &&
        !ingredientNames.includes("pork") &&
        !ingredientNames.includes("fish") &&
        !ingredientNames.includes("salmon") &&
        !ingredientNames.includes("tuna") &&
        !ingredientNames.includes("sausage")
      ) {
        if (
          !ingredientNames.includes("egg") &&
          !ingredientNames.includes("milk") &&
          !ingredientNames.includes("cheese") &&
          !ingredientNames.includes("butter") &&
          !ingredientNames.includes("yogurt") &&
          !ingredientNames.includes("cream")
        ) {
          detectedRestrictions.push("vegan");
        } else {
          detectedRestrictions.push("vegetarian");
        }
      }

      if (
        !ingredientNames.includes("milk") &&
        !ingredientNames.includes("cheese") &&
        !ingredientNames.includes("butter") &&
        !ingredientNames.includes("yogurt") &&
        !ingredientNames.includes("cream")
      ) {
        detectedRestrictions.push("dairy free");
      }

      if (
        !ingredientNames.includes("wheat") &&
        !ingredientNames.includes("flour") &&
        !ingredientNames.includes("bread") &&
        !ingredientNames.includes("pasta")
      ) {
        detectedRestrictions.push("gluten free");
      }

      // Check if recipe matches dietary restrictions
      const matchesRestrictions =
        dietaryRestrictions.length === 0 ||
        dietaryRestrictions.some((restriction) =>
          detectedRestrictions.includes(restriction),
        );

      return {
        ...recipe,
        detectedDietaryRestrictions: detectedRestrictions,
        matchesRestrictions,
      };
    });
  };

  const generateAdditionalRecipe = async (mealType, day, dayIndex) => {
    setGeneratingRecipe({ mealType, day, dayIndex });

    try {
      // Calculate current macros for this day to determine remaining needs
      const currentDayMacros = calculateDayMacros(day);
      const remainingProteins = Math.max(
        0,
        dailyProteins - currentDayMacros.totalP,
      );
      const remainingCarbs = Math.max(0, dailyCarbs - currentDayMacros.totalC);
      const remainingFats = Math.max(0, dailyFats - currentDayMacros.totalF);
      const remainingCalories = Math.max(
        0,
        dailyCalories - currentDayMacros.totalKCals,
      );

      // Get all existing recipe names across all meal types to exclude
      const existingRecipeNames = [];
      Object.keys(generatedMealPlan?.mealPlan || {}).forEach((mt) => {
        generatedMealPlan.mealPlan[mt].forEach((recipe) => {
          existingRecipeNames.push(recipe.n);
        });
      });

      // Analyze existing recipes for dietary compliance
      const analyzedRecipes = analyzeExistingRecipes(recipes, selectedDiets);

      // Convert dietary restrictions array to string
      const dietaryRestrictions =
        selectedDiets.length > 0 ? selectedDiets.join(", ") : "balanced";

      const messages = SingleRecipeForMealPlanMessage({
        mealType,
        dailyProteins:
          remainingProteins > 0
            ? remainingProteins
            : Math.round(dailyProteins / 4), // Target remaining or 1/4 of daily if already met
        dailyCarbs:
          remainingCarbs > 0 ? remainingCarbs : Math.round(dailyCarbs / 4),
        dailyFats:
          remainingFats > 0 ? remainingFats : Math.round(dailyFats / 4),
        dailyCalories:
          remainingCalories > 0
            ? remainingCalories
            : Math.round(dailyCalories / 4),
        dietaryRestrictions,
        customInstructions:
          customInstructions +
          (remainingProteins > 0 || remainingCarbs > 0 || remainingFats > 0
            ? ` Focus on filling remaining macros: ${remainingProteins}g protein, ${remainingCarbs}g carbs, ${remainingFats}g fats.`
            : ` This day is already meeting macro targets, create a balanced recipe for this meal type.`),
        existingRecipes: analyzedRecipes,
        excludeRecipeNames: existingRecipeNames,
      });

      const response = await getChatGPTResponse({
        messages: messages,
        temperature: 1.0,
        model: "meta-llama/llama-4-maverick-17b-128e-instruct",
        max_tokens: 8192,
        reasoning_effort: "medium"
      });

      // Extract JSON from the response
      let responseContent = response.response[0].message.content;

      // More comprehensive JSON extraction
      const extractJSON = (content) => {
        // Remove markdown code blocks
        content = content.replace(/```json\s*/g, "").replace(/```\s*/g, "").replace(/\s*```/g, "");
        
        // Try to find JSON object boundaries
        const jsonStart = content.indexOf('{');
        const jsonEnd = content.lastIndexOf('}');
        
        if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
          content = content.substring(jsonStart, jsonEnd + 1);
        }
        
        return content.trim();
      };

      responseContent = extractJSON(responseContent);
      
      let newRecipe;
      try {
        newRecipe = JSON.parse(responseContent);
      } catch (parseError) {
        consola.error("Failed to parse recipe JSON:", parseError);
        consola.error("Raw response content:", responseContent);
        
        // If parsing fails, show user-friendly error and retry
        throw new Error("The AI response was not in the expected format. Please try generating the recipe again.");
      }
      
      // Check if AI returned an error response (after successful parsing)
      if (newRecipe.error) {
        throw new Error(`Unable to generate recipe: ${newRecipe.error}. Try adjusting your dietary restrictions or macro targets.`);
      }

      // Add the new recipe to the meal plan
      setGeneratedMealPlan((prev) => {
        const newRecipeIndex = (prev.mealPlan[mealType] || []).length;
        const currentDayRecipes =
          prev.weeklySchedule?.[day.toLowerCase()]?.[mealType] || [];

        return {
          ...prev,
          mealPlan: {
            ...prev.mealPlan,
            [mealType]: [
              ...(prev.mealPlan[mealType] || []),
              { ...newRecipe, selected: true },
            ],
          },
          weeklySchedule: {
            ...prev.weeklySchedule,
            [day.toLowerCase()]: {
              ...prev.weeklySchedule[day.toLowerCase()],
              [mealType]: [...currentDayRecipes, newRecipeIndex],
            },
          },
        };
      });

      setGeneratingRecipe(null);
    } catch (error) {
      consola.error("Error generating additional recipe:", error);
      setGeneratingRecipe(null);
    }
  };

  const handleAddRecipe = (mealType, day, dayIndex) => {
    generateAdditionalRecipe(mealType, day, dayIndex);
  };

  const handleRemoveRecipe = (mealType, day, recipeIndex) => {
    setGeneratedMealPlan((prev) => {
      const currentDayRecipes =
        prev.weeklySchedule?.[day.toLowerCase()]?.[mealType] || [];
      const updatedDayRecipes = currentDayRecipes.filter(
        (_, index) => index !== recipeIndex,
      );

      return {
        ...prev,
        weeklySchedule: {
          ...prev.weeklySchedule,
          [day.toLowerCase()]: {
            ...(prev.weeklySchedule?.[day.toLowerCase()] || {}),
            [mealType]: updatedDayRecipes,
          },
        },
      };
    });
  };

  const handleAddExistingRecipe = (mealType, day, dayIndex) => {
    setRecipeSelectionDialog({ open: true, mealType, day, dayIndex });
  };

  const handleCopyMealToDay = (sourceMealType, sourceDay, targetDay) => {
    setGeneratedMealPlan((prev) => {
      // Get the recipe indexes from the source day/meal
      const sourceRecipeIndexes =
        prev.weeklySchedule?.[sourceDay.toLowerCase()]?.[sourceMealType] || [];

      // Get current recipe indexes for the target day/meal
      const currentTargetRecipes =
        prev.weeklySchedule?.[targetDay.toLowerCase()]?.[sourceMealType] || [];

      // Combine existing and new recipe indexes (avoiding duplicates)
      const combinedRecipeIndexes = [
        ...new Set([...currentTargetRecipes, ...sourceRecipeIndexes]),
      ];

      return {
        ...prev,
        weeklySchedule: {
          ...prev.weeklySchedule,
          [targetDay.toLowerCase()]: {
            ...(prev.weeklySchedule?.[targetDay.toLowerCase()] || {}),
            [sourceMealType]: combinedRecipeIndexes,
          },
        },
      };
    });
  };

  // Normalize macro data from Firebase recipes
  const normalizeMacrosFromRecipe = (recipe) => {
    // First check if we have top-level macros (Firebase format)
    if (
      recipe.kCals !== undefined ||
      recipe.proteins !== undefined ||
      recipe.carbs !== undefined ||
      recipe.fats !== undefined
    ) {
      return {
        kCals: recipe.kCals || 0,
        p: recipe.proteins || 0,
        c: recipe.carbs || 0,
        f: recipe.fats || 0,
      };
    }

    // If no top-level macros but we have ingredients, calculate from ingredients
    if (
      recipe.ingredients &&
      Array.isArray(recipe.ingredients) &&
      recipe.ingredients.length > 0
    ) {
      const calculated = recipe.ingredients.reduce(
        (totals, ingredient) => {
          return {
            kCals:
              totals.kCals +
              (ingredient.proteins || 0) * 4 +
              (ingredient.carbs || 0) * 4 +
              (ingredient.fats || 0) * 9,
            p: totals.p + (ingredient.proteins || 0),
            c: totals.c + (ingredient.carbs || 0),
            f: totals.f + (ingredient.fats || 0),
          };
        },
        { kCals: 0, p: 0, c: 0, f: 0 },
      );

      return calculated;
    }

    // Fallback to existing mac object or other field names
    const macroData = recipe.mac || recipe.macros || {};
    return {
      kCals: macroData.kCals || macroData.calories || macroData.cal || 0,
      p: macroData.p || macroData.protein || macroData.proteins || 0,
      c: macroData.c || macroData.carbs || macroData.carbohydrates || 0,
      f: macroData.f || macroData.fat || macroData.fats || 0,
    };
  };

  const handleSelectExistingRecipe = (selectedRecipe, sourceType) => {
    if (!recipeSelectionDialog) return;

    const { mealType, day } = recipeSelectionDialog;
    const recipeName = selectedRecipe.n || selectedRecipe.name;

    // Check if this recipe already exists in the meal plan
    const existingRecipeNames = new Set();
    Object.keys(generatedMealPlan.mealPlan).forEach((mt) => {
      generatedMealPlan.mealPlan[mt].forEach((recipe) => {
        existingRecipeNames.add(recipe.n);
      });
    });

    if (existingRecipeNames.has(recipeName)) {
      // Recipe already exists, find it and add reference to this day/meal
      let existingRecipeIndex = -1;
      const existingRecipes = generatedMealPlan.mealPlan[mealType] || [];

      for (let i = 0; i < existingRecipes.length; i++) {
        if (existingRecipes[i].n === recipeName) {
          existingRecipeIndex = i;
          break;
        }
      }

      if (existingRecipeIndex >= 0) {
        console.log("Adding reference to existing recipe:", {
          recipeName,
          existingMacros: existingRecipes[existingRecipeIndex].mac,
        });

        // Add reference to existing recipe
        setGeneratedMealPlan((prev) => {
          const currentDayRecipes =
            prev.weeklySchedule?.[day.toLowerCase()]?.[mealType] || [];

          return {
            ...prev,
            weeklySchedule: {
              ...prev.weeklySchedule,
              [day.toLowerCase()]: {
                ...(prev.weeklySchedule?.[day.toLowerCase()] || {}),
                [mealType]: [...currentDayRecipes, existingRecipeIndex],
              },
            },
          };
        });
      }
    } else {
      // Create a new recipe entry with proper macro handling
      const normalizedMacros = normalizeMacrosFromRecipe(selectedRecipe);

      const newRecipe = {
        ...selectedRecipe,
        selected: true,
        isExisting: sourceType === "existing",
        n: recipeName,
        mac: normalizedMacros,
        ns: selectedRecipe.servings || selectedRecipe.ns || 1,
      };

      console.log("Adding existing recipe with macros:", {
        recipeName,
        originalRecipe: selectedRecipe,
        normalizedMacros,
        servings: newRecipe.ns,
      });

      setGeneratedMealPlan((prev) => {
        const newRecipeIndex = (prev.mealPlan[mealType] || []).length;
        const currentDayRecipes =
          prev.weeklySchedule?.[day.toLowerCase()]?.[mealType] || [];

        return {
          ...prev,
          mealPlan: {
            ...prev.mealPlan,
            [mealType]: [...(prev.mealPlan[mealType] || []), newRecipe],
          },
          weeklySchedule: {
            ...prev.weeklySchedule,
            [day.toLowerCase()]: {
              ...(prev.weeklySchedule?.[day.toLowerCase()] || {}),
              [mealType]: [...currentDayRecipes, newRecipeIndex],
            },
          },
        };
      });
    }

    setRecipeSelectionDialog(null);
  };

  const handleCloseRecipeSelection = () => {
    setRecipeSelectionDialog(null);
  };

  const handleRecipePreview = (recipe) => {
    setRecipePreviewDialog({ open: true, recipe });
  };

  const handleCloseRecipePreview = () => {
    setRecipePreviewDialog(null);
  };

  // Calculate weekly meal plan completion summary
  const calculateWeeklyCompletionSummary = (mealPlan) => {
    if (!mealPlan?.weeklySchedule) return null;

    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const mealTypes = ['breakfast', 'lunch', 'dinner', 'snack'];
    
    let totalMeals = 0;
    let completedMeals = 0;
    let completedDays = 0;
    const dayStatus = {};

    days.forEach(day => {
      const dayMeals = mealPlan.weeklySchedule[day] || {};
      let dayCompletedMeals = 0;
      
      mealTypes.forEach(mealType => {
        totalMeals++;
        const meals = dayMeals[mealType] || [];
        if (meals.length > 0) {
          completedMeals++;
          dayCompletedMeals++;
        }
      });

      dayStatus[day] = {
        completed: dayCompletedMeals,
        total: 4,
        isComplete: dayCompletedMeals === 4
      };

      if (dayCompletedMeals === 4) {
        completedDays++;
      }
    });

    return {
      totalMeals,
      completedMeals,
      completedDays,
      totalDays: 7,
      dayStatus,
      percentComplete: Math.round((completedMeals / totalMeals) * 100)
    };
  };

  const generateMealPlan = async (targetDay = "monday") => {
    setGeneratingDay(targetDay);
    let response = null;

    try {
      // Analyze existing recipes for dietary compliance
      const analyzedRecipes = analyzeExistingRecipes(recipes, selectedDiets);

      // Convert dietary restrictions array to string
      const dietaryRestrictions =
        selectedDiets.length > 0 ? selectedDiets.join(", ") : "balanced";

      // Get existing recipe names to exclude
      const existingRecipeNames = [];
      Object.keys(generatedMealPlan?.mealPlan || {}).forEach((mt) => {
        generatedMealPlan.mealPlan[mt].forEach((recipe) => {
          existingRecipeNames.push(recipe.n);
        });
      });

      const messages = MealPlanGenerationMessage({
        dailyProteins,
        dailyCarbs,
        dailyFats,
        dailyCalories,
        dietaryRestrictions,
        customInstructions,
        existingRecipes: analyzedRecipes,
        excludeRecipeNames: existingRecipeNames,
        targetDay: targetDay.charAt(0).toUpperCase() + targetDay.slice(1),
      });

      response = await getChatGPTResponse({
        messages: messages,
        temperature: 1.0,
        model: "meta-llama/llama-4-maverick-17b-128e-instruct",
        max_tokens: 8192,
        reasoning_effort: "medium"
      });

      // Extract JSON from the response, handling markdown code blocks
      let responseContent = response.response[0].message.content;

      // Remove markdown code blocks if present
      if (responseContent.includes("```json")) {
        responseContent = responseContent
          .replace(/```json\s*/g, "")
          .replace(/\s*```/g, "");
      } else if (responseContent.includes("```")) {
        responseContent = responseContent
          .replace(/```\s*/g, "")
          .replace(/\s*```/g, "");
      }

      // Clean up any extra whitespace
      responseContent = responseContent.trim();

      consola.log("Cleaned response content:", responseContent);

      const contentObject = JSON.parse(responseContent);

      // Validate that required fields exist
      if (!contentObject.mealPlan) {
        throw new Error("Missing mealPlan field in response");
      }

      // Ensure all meal types exist in the response
      const requiredMealTypes = ["breakfast", "lunch", "dinner", "snack"];
      requiredMealTypes.forEach((mealType) => {
        if (!contentObject.mealPlan[mealType]) {
          contentObject.mealPlan[mealType] = [];
        }
      });

      // Add selected property to all recipes (default to true)
      Object.keys(contentObject.mealPlan).forEach((mealType) => {
        contentObject.mealPlan[mealType].forEach((recipe) => {
          recipe.selected = true;
        });
      });

      // Merge new recipes into existing meal plan
      setGeneratedMealPlan((prev) => {
        const newMealPlan = { ...prev.mealPlan };
        const newWeeklySchedule = { ...prev.weeklySchedule };

        // Add new recipes to meal plan
        Object.keys(contentObject.mealPlan).forEach((mealType) => {
          contentObject.mealPlan[mealType].forEach((recipe) => {
            const recipeIndex = newMealPlan[mealType].length;
            newMealPlan[mealType].push(recipe);

            // Add to target day's schedule
            if (
              !newWeeklySchedule[targetDay.toLowerCase()][mealType].includes(
                recipeIndex,
              )
            ) {
              newWeeklySchedule[targetDay.toLowerCase()][mealType].push(
                recipeIndex,
              );
            }
          });
        });

        return {
          ...prev,
          mealPlan: newMealPlan,
          weeklySchedule: newWeeklySchedule,
          analysis: contentObject.analysis || prev.analysis,
        };
      });

      setGeneratingDay(null);
    } catch (error) {
      consola.error("Error generating meal plan:", error);
      consola.error("Raw response:", response?.response?.[0]?.message?.content);
      setGeneratingDay(null);

      // You could add user-friendly error handling here
      // For example, show a snackbar or alert with the error
    }
  };

  const handleRecipeToggle = (mealType, recipeIndex, selected) => {
    setGeneratedMealPlan((prev) => ({
      ...prev,
      mealPlan: {
        ...prev.mealPlan,
        [mealType]: prev.mealPlan[mealType].map((recipe, index) =>
          index === recipeIndex ? { ...recipe, selected } : recipe,
        ),
      },
    }));
  };

  const getSelectedRecipes = () => {
    if (!generatedMealPlan) return [];

    const selected = [];
    const uniqueRecipeNames = new Set();

    Object.keys(generatedMealPlan.mealPlan).forEach((mealType) => {
      generatedMealPlan.mealPlan[mealType].forEach((recipe) => {
        if (recipe.selected !== false && !uniqueRecipeNames.has(recipe.n)) {
          uniqueRecipeNames.add(recipe.n);
          selected.push(recipe);
        }
      });
    });
    return selected;
  };

  const getAllGeneratedRecipes = () => {
    if (!generatedMealPlan) return [];

    const allRecipes = [];
    Object.keys(generatedMealPlan.mealPlan).forEach((mealType) => {
      generatedMealPlan.mealPlan[mealType].forEach((recipe) => {
        // Only include AI-generated recipes, not existing recipes from the library
        if (!recipe.isExisting) {
          allRecipes.push(recipe);
        }
      });
    });
    return allRecipes;
  };

  // Calculate daily averages for the meal plan totals
  const calculateMealPlanTotals = () => {
    const dayNames = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    let totalDays = 0;
    let sumKCals = 0,
      sumProteins = 0,
      sumCarbs = 0,
      sumFats = 0,
      sumFiber = 0;

    dayNames.forEach((day) => {
      const dayMacros = calculateDayMacros(day);

      // Only count days that have at least one recipe assigned
      if (dayMacros.completedMeals > 0) {
        totalDays++;
        sumKCals += dayMacros.totalKCals;
        sumProteins += dayMacros.totalP;
        sumCarbs += dayMacros.totalC;
        sumFats += dayMacros.totalF;
        sumFiber += dayMacros.totalFi;
      }
    });

    // Return averages if we have data, otherwise return zeros
    if (totalDays > 0) {
      return {
        totalkCals: Math.round(sumKCals / totalDays),
        totalProteins: Math.round(sumProteins / totalDays),
        totalCarbs: Math.round(sumCarbs / totalDays),
        totalFats: Math.round(sumFats / totalDays),
        totalFiber: Math.round(sumFiber / totalDays),
        daysWithRecipes: totalDays,
      };
    }

    return {
      totalkCals: 0,
      totalProteins: 0,
      totalCarbs: 0,
      totalFats: 0,
      totalFiber: 0,
      daysWithRecipes: 0,
    };
  };

  // Transform local meal plan state to Firebase-compatible format
  const transformMealPlanForFirebase = () => {
    if (!generatedMealPlan?.mealPlan || !generatedMealPlan?.weeklySchedule) {
      throw new Error("Invalid meal plan state");
    }

    // Calculate meaningful totals as daily averages
    const calculatedTotals = calculateMealPlanTotals();

    const transformedData = {
      newRecipes: [], // AI-generated recipes that need to be created
      selectedRecipes: [], // Firebase format: array of recipe assignments
      isEditMode: isEditMode, // Add edit mode flag
      mealPlanMetadata: {
        name:
          mealPlanName ||
          (isEditMode
            ? existingMealPlan?.name || "Updated Meal Plan"
            : "AI Generated Meal Plan"),
        description:
          mealPlanDescription ||
          `${isEditMode ? "Updated meal plan" : "Generated meal plan"} with ${dailyProteins}g protein, ${dailyCarbs}g carbs, ${dailyFats}g fats`,
        isPublic: isPublic,
        macroTargets: {
          protein: dailyProteins,
          carbs: dailyCarbs,
          fats: dailyFats,
          calories: dailyCalories,
        },
        calculatorSettings: {
          useBulkMode: useBulkMode,
          bulkCalories: bulkCalories,
          bulkProteinPercent: bulkProteinPercent,
          bulkCarbPercent: bulkCarbPercent,
          bulkFatPercent: bulkFatPercent,
        },
        dietaryRestrictions: selectedDiets,
        customInstructions,
        // Add calculated totals to metadata
        totals: calculatedTotals,
      },
    };

    // In edit mode, preserve additional metadata
    if (isEditMode && existingMealPlan) {
      transformedData.mealPlanMetadata = {
        ...transformedData.mealPlanMetadata,
        id: existingMealPlan.id,
        createdOn: existingMealPlan.createdOn,
        updatedOn: new Date().toISOString(),
        // Preserve any other existing fields
        ...Object.keys(existingMealPlan).reduce((acc, key) => {
          if (
            ![
              "name",
              "description",
              "isPublic",
              "macroTargets",
              "dietaryRestrictions",
              "customInstructions",
              "calculatorSettings",
            ].includes(key)
          ) {
            acc[key] = existingMealPlan[key];
          }
          return acc;
        }, {}),
      };
    }

    // Create a map to track unique recipes and their assignments
    const recipeAssignments = new Map();

    // Process each meal type and build recipe assignments
    Object.keys(generatedMealPlan.mealPlan).forEach((mealType) => {
      generatedMealPlan.mealPlan[mealType].forEach((recipe, recipeIndex) => {
        if (recipe.selected === false) return; // Skip unselected recipes

        // Find which days this recipe is assigned to for this meal type
        const assignedDays = [];
        Object.keys(generatedMealPlan.weeklySchedule).forEach((day) => {
          const daySchedule =
            generatedMealPlan.weeklySchedule[day][mealType] || [];
          if (daySchedule.includes(recipeIndex)) {
            assignedDays.push(day);
          }
        });

        if (assignedDays.length === 0) return; // Skip if not assigned to any days

        // Create a unique key for this recipe-mealtype combination
        const recipeKey = `${recipe.id || recipe.n}_${mealType}`;

        if (!recipeAssignments.has(recipeKey)) {
          const days = assignedDays.length === 7 ? ["all"] : assignedDays;

          if (recipe.isExisting && recipe.id) {
            // This is an existing recipe from the library
            transformedData.selectedRecipes.push({
              recipeId: recipe.id,
              name: recipe.n || recipe.name,
              days: days,
              servingsPerDay: recipe.ns || 1,
              id: nanoid(), // Use nanoid for better uniqueness and consistency
            });
          } else {
            // This is a new AI-generated recipe
            const newRecipeId = `generated-recipe-${nanoid()}`;

            console.log("Processing AI-generated recipe for Firebase:", {
              originalRecipe: recipe,
              hasDescription: !!recipe.d,
              hasInstructions: !!recipe.i,
              hasIngredients: !!recipe.ing,
              instructionsType: Array.isArray(recipe.i)
                ? "array"
                : typeof recipe.i,
            });

            // Convert AI recipe format to Firebase format
            const firebaseRecipe = {
              ...recipe,
              id: newRecipeId,
              mealType,
              // Map AI response fields to Firebase expected fields
              name: recipe.n || recipe.name,
              description: recipe.d || recipe.description || "",
              instructions: Array.isArray(recipe.i)
                ? recipe.i.map((instruction) => {
                    // Preserve instruction objects with their structure
                    if (
                      typeof instruction === "object" &&
                      instruction !== null
                    ) {
                      return {
                        id: instruction.id || `instruction-${nanoid()}`,
                        instruction:
                          instruction.instruction ||
                          instruction.step ||
                          String(instruction),
                        duration: instruction.duration || 0,
                      };
                    }
                    // Convert string instructions to proper object format
                    return {
                      id: `instruction-${nanoid()}`,
                      instruction: String(instruction),
                      duration: 0,
                    };
                  })
                : recipe.instructions || [],
              ingredients: Array.isArray(recipe.ing)
                ? recipe.ing.map((ingredient) => ({
                    name: ingredient.n || ingredient.name,
                    amount: ingredient.q || ingredient.amount || 1,
                    unit: ingredient.u || ingredient.unit || "g",
                    category:
                      ingredient.category ||
                      suggestCategory(ingredient.n || ingredient.name || ""),
                    proteins: ingredient.mac?.p || ingredient.proteins || 0,
                    carbs: ingredient.mac?.c || ingredient.carbs || 0,
                    fats: ingredient.mac?.f || ingredient.fats || 0,
                    fiber:
                      ingredient.mac?.fi ??
                      ingredient.fiber ??
                      0,
                  }))
                : recipe.ingredients || [],
              servings: recipe.ns || recipe.servings || 1,
              macros: recipe.mac ||
                recipe.macros || { kCals: 0, p: 0, c: 0, f: 0 },
              fiber:
                recipe.fiber ??
                recipe.mac?.fi ??
                recipe.mac?.fiber ??
                0,
            };

            console.log("Transformed recipe for Firebase:", {
              name: firebaseRecipe.name,
              hasDescription: !!firebaseRecipe.description,
              hasInstructions: !!firebaseRecipe.instructions,
              ingredientsCount: firebaseRecipe.ingredients?.length || 0,
              description: firebaseRecipe.description,
              instructions: firebaseRecipe.instructions,
              tempId: newRecipeId,
            });

            // Add to new recipes list
            transformedData.newRecipes.push(firebaseRecipe);

            // Add to selected recipes list
            transformedData.selectedRecipes.push({
              recipeId: newRecipeId, // Use the same temp ID here
              name: recipe.n,
              days: days,
              servingsPerDay: recipe.ns || 1,
              id: nanoid(), // Use nanoid for better uniqueness and consistency
            });

            console.log(
              "Added selectedRecipe entry with temp recipeId:",
              newRecipeId,
            );
          }

          recipeAssignments.set(recipeKey, true);
        }
      });
    });

    return transformedData;
  };

  return (
    <Dialog
      maxWidth="xl"
      fullWidth={true}
      open={true}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || isSaving) {
          return;
        }
        if (onDialogClosed) {
          onDialogClosed(null);
        }
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: `linear-gradient(135deg, ${NewColors.active.primary} 0%, ${NewColors.active.primaryButton} 100%)`,
          color: "white",
          textAlign: "center",
          fontSize: "1.5rem",
          fontWeight: 600,
        }}
      >
        {isEditMode ? "Edit Meal Plan" : "Meal Plan Generator"}
      </DialogTitle>

      <DialogContent sx={{ p: 3, pb: 1 }}>
        <Typography
          variant="body1"
          sx={{
            mb: 2,
            textAlign: "center",
            color: "text.secondary",
            fontSize: "0.9rem",
          }}
        >
          {isEditMode
            ? 'Edit your meal plan settings and recipes. Use the "Generate" buttons to add AI recipes for specific days and meal types, or browse existing recipes to modify your meal plan.'
            : 'Set your daily macro targets and dietary preferences. Use the "Generate" buttons on each day to create AI recipes for specific days and meal types, or browse existing recipes to build your meal plan manually.'}
        </Typography>

        <Grid container spacing={2}>
          {leftPanelCollapsed ? (
            // Collapsed state - thin column with expand button
            <Grid item xs={12} md={0.5} sx={{ display: { xs: 'none', md: 'block' } }}>
              <Paper 
                elevation={2} 
                sx={{ 
                  height: "fit-content",
                  minHeight: "200px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  p: 1,
                  pt: 1,
                  bgcolor: "primary.main",
                  color: "primary.contrastText"
                }}
              >
                <IconButton
                  onClick={() => setLeftPanelCollapsed(false)}
                  size="small"
                  sx={{
                    backgroundColor: "primary.contrastText",
                    color: "primary.main",
                    "&:hover": { 
                      backgroundColor: "background.paper",
                      transform: "scale(1.05)"
                    },
                    transition: "all 0.2s ease-in-out",
                    width: 24,
                    height: 24,
                    minWidth: 24,
                    mb: 1.5
                  }}
                >
                  <ExpandIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    writingMode: "vertical-rl", 
                    textOrientation: "mixed",
                    fontSize: "0.7rem",
                    fontWeight: 500
                  }}
                >
                  Settings
                </Typography>
              </Paper>
            </Grid>
          ) : (
            // Expanded state - full panel
            <Grid item xs={12} md={3}>
              <Paper elevation={2} sx={{ p: 1.5, height: "fit-content", position: "relative" }}>
                {/* Collapse Button */}
                <IconButton
                  onClick={() => setLeftPanelCollapsed(true)}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 10,
                    backgroundColor: "background.paper",
                    "&:hover": { 
                      backgroundColor: "action.hover",
                      transform: "scale(1.05)"
                    },
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: 1,
                    transition: "all 0.2s ease-in-out"
                  }}
                >
                  <CollapseIcon />
                </IconButton>

                {/* Meal Plan Name - moved to top with proper spacing */}
                <TextField
                fullWidth
                label="Meal Plan Name"
                value={mealPlanName}
                onChange={(e) => setMealPlanName(e.target.value)}
                size="small"
                sx={{ mb: 2, mt: 1, pr: 6 }} // Added right padding to avoid button overlap
                placeholder={
                  isEditMode
                    ? existingMealPlan?.name || "e.g., Updated Weekly Plan"
                    : "e.g., Weekly Macro Plan"
                }
                required={!isEditMode} // Only required for new meal plans
              />

              {/* Meal Plan Description */}
              <TextField
                fullWidth
                label="Description"
                value={mealPlanDescription}
                onChange={(e) => setMealPlanDescription(e.target.value)}
                size="small"
                multiline
                rows={3}
                sx={{ mb: 2, pr: 6 }} // Added right padding to avoid button overlap
                placeholder="Describe this meal plan..."
              />

              {/* Public Access Toggle */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                      Make Public
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                    >
                      Allow viewing without login
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2, alignItems: "flex-start" }}
              />

              <Typography
                variant="h6"
                sx={{
                  mb: 1.5,
                  color: "primary.main",
                  fontWeight: 600,
                  fontSize: "1.1rem",
                }}
              >
                Daily Macro Targets
              </Typography>

              {/* Mode Toggle */}
              <Card sx={{ mb: 1, p: 0.25 }}>
                <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useBulkMode}
                        onChange={(e) => setUseBulkMode(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        {useBulkMode
                          ? "Calorie + Ratio Mode"
                          : "Direct Grams Mode"}
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      color: "text.secondary",
                      display: "block",
                      mt: 0.5,
                    }}
                  >
                    {useBulkMode
                      ? "Calculate macros from total calories and percentages"
                      : "Enter macro grams directly"}
                  </Typography>
                </CardContent>
              </Card>

              {useBulkMode ? (
                <CalorieBasedMacroCalculator
                  dailyCalories={bulkCalories}
                  onCaloriesChange={setBulkCalories}
                  proteinPercent={bulkProteinPercent}
                  onProteinPercentChange={setBulkProteinPercent}
                  carbPercent={bulkCarbPercent}
                  onCarbPercentChange={setBulkCarbPercent}
                  fatPercent={bulkFatPercent}
                  onFatPercentChange={setBulkFatPercent}
                  onApplyMacros={handleApplyBulkMacros}
                  selectedDiets={selectedDiets}
                />
              ) : (
                <>
                  <MacroInputStack
                    label="Protein"
                    value={dailyProteins}
                    onChange={setDailyProteins}
                  />

                  <MacroInputStack
                    label="Carbohydrates"
                    value={dailyCarbs}
                    onChange={setDailyCarbs}
                  />

                  <MacroInputStack
                    label="Fats"
                    value={dailyFats}
                    onChange={setDailyFats}
                  />
                </>
              )}

              <Card sx={{ mb: 1, p: 0.25, bgcolor: NewColors.nutrition.caloriesBackground }}>
                <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, fontSize: "0.8rem", color: NewColors.nutrition.calories }}
                  >
                    Daily Calories: {dailyCalories} cal
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "0.65rem", color: NewColors.text.secondary }}
                  >
                    {useBulkMode
                      ? "From calorie calculator"
                      : "Calculated from macro targets"}
                  </Typography>
                </CardContent>
              </Card>

              <DietaryRestrictionsSelector
                selectedDiets={selectedDiets}
                onDietsChanged={setSelectedDiets}
              />

              <CustomInstructionsInput
                customInstructions={customInstructions}
                onCustomInstructionsChanged={setCustomInstructions}
                onGenerateDay={generateMealPlan}
                generatingDay={generatingDay}
              />
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} md={leftPanelCollapsed ? 11.5 : 9}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                height: "fit-content",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 2, color: "primary.main", fontWeight: 600 }}
              >
                Generated Meal Plan
              </Typography>

              {generatedMealPlan && (
                <Box sx={{ flex: 1, overflow: "auto" }}>
                  {(() => {
                    const completionSummary = calculateWeeklyCompletionSummary(generatedMealPlan);
                    if (!completionSummary) return null;

                    const isFullyComplete = completionSummary.percentComplete === 100;
                    const bgColor = isFullyComplete ? "success.light" : 
                                   completionSummary.percentComplete >= 50 ? "warning.light" : NewColors.sectionBackground;
                    const textColor = isFullyComplete ? "white" : NewColors.text.primary;

                    return (
                      <Card sx={{ mb: 2, bgcolor: bgColor }}>
                        <CardContent sx={{ p: 2 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, color: textColor, mb: 1 }}
                          >
                            📋 Weekly Meal Plan Progress
                          </Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2" sx={{ color: textColor }}>
                                <strong>Overall Progress:</strong>{" "}
                                {completionSummary.completedMeals}/{completionSummary.totalMeals} meals ({completionSummary.percentComplete}%)
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" sx={{ color: textColor }}>
                                <strong>Complete Days:</strong>{" "}
                                {completionSummary.completedDays}/{completionSummary.totalDays} days
                              </Typography>
                            </Grid>
                            {!isFullyComplete && (
                              <Grid item xs={12}>
                                <Typography variant="body2" sx={{ color: textColor, mt: 1, fontStyle: "italic" }}>
                                  {completionSummary.completedDays === 0 
                                    ? "Get started by generating meals for each day!" 
                                    : `${completionSummary.totalMeals - completionSummary.completedMeals} more meals needed to complete your weekly plan.`
                                  }
                                </Typography>
                              </Grid>
                            )}
                          </Grid>
                        </CardContent>
                      </Card>
                    );
                  })()}

                  <WeeklyMealPlanView
                    generatedMealPlan={generatedMealPlan}
                    onRecipeToggle={handleRecipeToggle}
                    onAddRecipe={handleAddRecipe}
                    onRemoveRecipe={handleRemoveRecipe}
                    onAddExistingRecipe={handleAddExistingRecipe}
                    onGenerateDay={generateMealPlan}
                    onCopyMealToDay={handleCopyMealToDay}
                    onRecipePreview={handleRecipePreview}
                    generatingRecipe={generatingRecipe}
                    generatingDay={generatingDay}
                    dailyProteins={dailyProteins}
                    dailyCarbs={dailyCarbs}
                    dailyFats={dailyFats}
                    calculateDayMacros={calculateDayMacros}
                  />
                </Box>
              )}

              {!generatedMealPlan && (
                <Stack
                  spacing={3}
                  sx={{
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Box sx={{ fontSize: "4rem" }}>🍽️</Box>
                  <Typography variant="h5" sx={{ fontWeight: 500 }}>
                    Ready to Create Your Meal Plan?
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ maxWidth: "80%" }}
                  >
                    Set your daily macro targets and dietary preferences on the
                    left, then use the &quot;Generate&quot; buttons on each day
                    to create AI recipes or browse existing recipes to build
                    your plan!
                  </Typography>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 1.5, pt: 0.5, mt: 2 }}>
        <Button
          onClick={() => {
            if (onDialogClosed) {
              onDialogClosed(null);
            }
          }}
          disabled={isSaving}
          size="large"
          sx={{ px: 3 }}
        >
          Cancel
        </Button>
        <StandardButton
          variant="primary"
          onClick={async () => {
            try {
              if (onDialogClosed && generatedMealPlan && !isSaving) {
                setIsSaving(true);
                
                // Transform local state to Firebase-compatible format
                const transformedData = transformMealPlanForFirebase();
                console.log(
                  "Transformed meal plan data for save:",
                  transformedData,
                );
                console.log("Is edit mode:", isEditMode);
                console.log("Existing meal plan ID:", existingMealPlan?.id);

                // Add edit mode metadata
                if (isEditMode) {
                  transformedData.isEditMode = true;
                  transformedData.existingMealPlanId = existingMealPlan.id;
                }

                // Pass the properly structured data to the parent
                await onDialogClosed(transformedData);
              }
            } catch (error) {
              console.error("Error transforming meal plan data:", error);
              setIsSaving(false);
              // Still close dialog but with null data to indicate error
              if (onDialogClosed) {
                await onDialogClosed(null);
              }
            }
          }}
          disabled={
            !generatedMealPlan ||
            getSelectedRecipes().length === 0 ||
            (!isEditMode && !mealPlanName.trim()) ||
            isSaving
          }
          size="large"
          sx={{ px: 3 }}
        >
          {isSaving 
            ? "Saving..." 
            : `${isEditMode ? "Update Meal Plan" : "Use This Meal Plan"} (${generatedMealPlan ? getSelectedRecipes().length : 0} recipes)`
          }
        </StandardButton>
      </DialogActions>

      <RecipeSelectionDialog
        open={recipeSelectionDialog?.open || false}
        onClose={handleCloseRecipeSelection}
        recipes={recipes}
        generatedRecipes={getAllGeneratedRecipes()}
        onSelectRecipe={handleSelectExistingRecipe}
        mealType={recipeSelectionDialog?.mealType || ""}
      />

      <RecipePreviewDialog
        open={recipePreviewDialog?.open || false}
        onClose={handleCloseRecipePreview}
        recipe={recipePreviewDialog?.recipe}
      />
    </Dialog>
  );
};

export default MealPlanGeneratorDialog;
